export type CommercialConfig = {
  /** Legacy (obligatoire côté Prisma) */
  nomEntreprise: string;
  slogan?: string | null;
  adresse?: string | null;
  telephone?: string | null;
  email?: string | null;
  siteWeb?: string | null;
  rccm?: string | null;
  nif?: string | null;
  idNat?: string | null;
  logo?: string | null;
  devise?: string | null;
  tauxTVA?: number | null;
  tauxChange?: number | null;
  numerotationFacturePrefix?: string | null;
  numerotationProformaPrefix?: string | null;
  numerotationQuittancePrefix?: string | null;
  conditionsPaiement?: string | null;
  mentionFacture?: string | null;
  mentionsLegales?: string | null;
  signatureCachet?: string | null;
  mentionProforma?: string | null;
  mentionQuittance?: string | null;

  nomLegal?: string | null;
  nomCommercial?: string | null;
  adresseComplete?: string | null;
  logoUrl?: string | null;
  numeroImpotTva?: string | null;
  banque?: string | null;
  nomCompte?: string | null;
  numeroCompte?: string | null;
  iban?: string | null;
  swift?: string | null;
  deviseCompte?: string | null;
  mobileMoneyEntreprise?: string | null;
  formatNumero?: string | null;
  prochainNumeroFacture?: number | null;
  prochainNumeroProforma?: number | null;
  prochainNumeroQuittance?: number | null;
  mentionTva?: string | null;
  exonereTva?: boolean;
  dateTauxChange?: string | null;
  delaiPaiementJours?: number | null;
  penaliteRetard?: string | null;
  validiteProformaJours?: number | null;
  clauseContestation?: string | null;
  piedPageFacture?: string | null;
  directorGeneralName?: string | null;
  directorGeneralTitle?: string | null;
  nomSignataire?: string | null;
  fonctionSignataire?: string | null;
  signatureCachetUrl?: string | null;
  /** Alias affichage = `devise` */
  deviseDefaut?: string | null;
  tauxTva?: number | null;
};

export const DEFAULT_FORMAT_NUMERO = "{PREFIX}-YYYY-{SEQ:5}";

/** Textes d’exemple (alignés sur `defaultCommercialConfiguration` côté API) — mode brouillon / hors connexion. */
export const DEFAULT_COMMERCIAL_CONFIG: CommercialConfig = {
  nomEntreprise: "EspoirAfriqueSas",
  nomLegal: "EspoirAfrique Société par Actions Simplifiée",
  nomCommercial: "Espoir Afrique",
  slogan: "Services professionnels — gestion environnementale et urbaine",
  adresse: "123 Avenue du Commerce, Kinshasa — Gombe",
  adresseComplete:
    "123 Avenue du Commerce\nCommune de la Gombe\nKinshasa\nRépublique Démocratique du Congo",
  telephone: "+243 900 000 000",
  email: "contact@espoirafrique.cd",
  siteWeb: "https://www.espoirafrique.cd",
  rccm: "RCCM/CD/KIN/RCCM/00-A-00000",
  nif: "A0700000000X",
  idNat: "01-9-N00000X",
  numeroImpotTva: "A07001234X",
  logo: "",
  logoUrl: "",
  devise: "USD",
  deviseDefaut: "USD",
  tauxTVA: 16,
  tauxTva: 16,
  tauxChange: 1,
  numerotationFacturePrefix: "FAC",
  numerotationProformaPrefix: "PRO",
  numerotationQuittancePrefix: "QUI",
  formatNumero: DEFAULT_FORMAT_NUMERO,
  prochainNumeroFacture: 1,
  prochainNumeroProforma: 1,
  prochainNumeroQuittance: 1,
  exonereTva: false,
  mentionTva: "TVA appliquée conformément à la législation fiscale en vigueur.",
  banque: "Rawbank SA",
  nomCompte: "Espoir Afrique SAS — compte principal",
  numeroCompte: "00012-3456789-01",
  iban: "CD123456789012345678901234",
  swift: "RAWBCDKX",
  deviseCompte: "USD",
  mobileMoneyEntreprise: "M-Pesa (entreprise) : +243 900 000 001",
  conditionsPaiement:
    "Paiement par virement bancaire. Aucun escompte pour paiement anticipé.",
  delaiPaiementJours: 30,
  penaliteRetard:
    "En cas de retard de paiement, des intérêts de retard peuvent être facturés au taux légal en vigueur.",
  validiteProformaJours: 30,
  clauseContestation:
    "Toute contestation relative au présent document doit être formulée par écrit dans les 8 jours suivant sa réception.",
  piedPageFacture:
    "Merci pour votre confiance. Capital social et statuts disponibles au siège social.",
  mentionFacture: "Facture établie conformément aux conditions générales de vente.",
  mentionsLegales:
    "Société immatriculée au RCCM. Les prix sont exprimés en devise indiquée, nets de taxes applicables.",
  signatureCachet: "",
  mentionProforma: "Ce document est une offre indicative et ne vaut pas facture.",
  mentionQuittance:
    "Quittance délivrée pour solde de tout compte concernant la facture citée.",
  directorGeneralName: "Percy Tuluka",
  directorGeneralTitle: "Directeur général",
  nomSignataire: "Percy Tuluka",
  fonctionSignataire: "Directeur général",
  signatureCachetUrl: "",
};

function n(v: unknown, d: string): string {
  if (v == null) return d;
  return String(v);
}

function nn(v: unknown, d: number | null): number | null {
  if (v == null || v === "") return d;
  const x = Number(v);
  return Number.isFinite(x) ? x : d;
}

function nb(v: unknown, d: boolean): boolean {
  if (typeof v === "boolean") return v;
  if (v === "true" || v === 1) return true;
  if (v === "false" || v === 0) return false;
  return d;
}

function dateToInputVal(v: unknown): string {
  if (v == null || v === "") return "";
  if (typeof v === "string") {
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
  }
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return "";
}

/** Nom affiché : priorité commerciale / légal / legacy. */
export function getDisplayCompanyName(c: CommercialConfig): string {
  const a = c.nomCommercial?.trim();
  if (a) return a;
  const b = c.nomLegal?.trim();
  if (b) return b;
  return c.nomEntreprise?.trim() || "Entreprise";
}

export function getDisplayAddress(c: CommercialConfig): string {
  const a = c.adresseComplete?.trim();
  if (a) return a;
  return c.adresse?.trim() || "";
}

export function getLogoUrl(c: CommercialConfig): string {
  return (c.logoUrl || c.logo || "").trim();
}

export function getDevise(c: CommercialConfig): string {
  return (c.deviseDefaut || c.devise || "USD").trim() || "USD";
}

export function getTauxTvaPercent(c: CommercialConfig): number {
  const t = c.tauxTva ?? c.tauxTVA;
  if (t == null || !Number.isFinite(t)) return 16;
  return t;
}

export function getSignatureImageUrl(c: CommercialConfig): string {
  return (c.signatureCachetUrl || c.signatureCachet || "").trim();
}

export function getPiedPageFacture(c: CommercialConfig): string {
  return (c.piedPageFacture || c.mentionFacture || "").trim();
}

/**
 * Aperçu : numéro selon `formatNumero` (défaut `{PREFIX}-YYYY-{SEQ:5}`).
 * Remplace : `{PREFIX}`, `YYYY`, `{SEQ:n}`, `{SEQ}`.
 */
export function formatDemoDocumentNumber(
  config: CommercialConfig,
  kind: "facture" | "proforma" | "quittance",
  year = new Date().getFullYear()
): string {
  const prefix =
    kind === "facture"
      ? config.numerotationFacturePrefix || "FAC"
      : kind === "proforma"
        ? config.numerotationProformaPrefix || "PRO"
        : config.numerotationQuittancePrefix || "QUI";
  const seq =
    kind === "facture"
      ? (config.prochainNumeroFacture ?? 1)
      : kind === "proforma"
        ? (config.prochainNumeroProforma ?? 1)
        : (config.prochainNumeroQuittance ?? 1);
  const tpl = (config.formatNumero || DEFAULT_FORMAT_NUMERO).trim() || DEFAULT_FORMAT_NUMERO;
  return tpl
    .replace(/\{PREFIX\}/g, prefix)
    .replace(/YYYY/g, String(year))
    .replace(/\{SEQ:(\d+)\}/g, (_, w) => String(seq).padStart(Number(w), "0"))
    .replace(/\{SEQ\}/g, String(seq).padStart(5, "0"));
}

export function normalizeCommercialConfig(data: Record<string, unknown>): CommercialConfig {
  const deviseVal = n(data.deviseDefaut ?? data.devise, DEFAULT_COMMERCIAL_CONFIG.devise ?? "USD");
  const taux =
    data.tauxTva != null ? nn(data.tauxTva, 16) : nn(data.tauxTVA, 16);
  const mergedLogo =
    [data.logoUrl, data.logo]
      .map((x) => (x != null && String(x).trim() ? String(x).trim() : ""))
      .find((s) => s.length > 0) || "";

  return {
    nomEntreprise: n(data.nomEntreprise, DEFAULT_COMMERCIAL_CONFIG.nomEntreprise),
    slogan: data.slogan != null ? String(data.slogan) : "",
    adresse: data.adresse != null ? String(data.adresse) : "",
    telephone: data.telephone != null ? String(data.telephone) : "",
    email: data.email != null ? String(data.email) : "",
    siteWeb: data.siteWeb != null ? String(data.siteWeb) : "",
    rccm: data.rccm != null ? String(data.rccm) : "",
    nif: data.nif != null ? String(data.nif) : "",
    idNat: data.idNat != null ? String(data.idNat) : "",
    logo: mergedLogo,
    logoUrl: mergedLogo,
    devise: deviseVal,
    deviseDefaut: deviseVal,
    tauxTVA: taux,
    tauxTva: taux,
    tauxChange: nn(data.tauxChange, 1),
    numerotationFacturePrefix:
      data.prefixeFacture != null && String(data.prefixeFacture).trim()
        ? String(data.prefixeFacture)
        : data.numerotationFacturePrefix != null
          ? String(data.numerotationFacturePrefix)
          : "FAC",
    numerotationProformaPrefix:
      data.prefixeProforma != null && String(data.prefixeProforma).trim()
        ? String(data.prefixeProforma)
        : data.numerotationProformaPrefix != null
          ? String(data.numerotationProformaPrefix)
          : "PRO",
    numerotationQuittancePrefix:
      data.prefixeQuittance != null && String(data.prefixeQuittance).trim()
        ? String(data.prefixeQuittance)
        : data.numerotationQuittancePrefix != null
          ? String(data.numerotationQuittancePrefix)
          : "QUI",
    formatNumero:
      data.formatNumero != null && String(data.formatNumero).trim() !== ""
        ? String(data.formatNumero)
        : DEFAULT_FORMAT_NUMERO,
    prochainNumeroFacture: nn(data.prochainNumeroFacture, 1),
    prochainNumeroProforma: nn(data.prochainNumeroProforma, 1),
    prochainNumeroQuittance: nn(data.prochainNumeroQuittance, 1),
    conditionsPaiement: data.conditionsPaiement != null ? String(data.conditionsPaiement) : "",
    mentionFacture: data.mentionFacture != null ? String(data.mentionFacture) : "",
    mentionsLegales: data.mentionsLegales != null ? String(data.mentionsLegales) : "",
    signatureCachet: data.signatureCachet != null ? String(data.signatureCachet) : "",
    mentionProforma: data.mentionProforma != null ? String(data.mentionProforma) : "",
    mentionQuittance: data.mentionQuittance != null ? String(data.mentionQuittance) : "",

    nomLegal: data.nomLegal != null ? String(data.nomLegal) : "",
    nomCommercial: data.nomCommercial != null ? String(data.nomCommercial) : "",
    adresseComplete: data.adresseComplete != null ? String(data.adresseComplete) : "",
    numeroImpotTva: data.numeroImpotTva != null ? String(data.numeroImpotTva) : "",
    banque: data.banque != null ? String(data.banque) : "",
    nomCompte: data.nomCompte != null ? String(data.nomCompte) : "",
    numeroCompte: data.numeroCompte != null ? String(data.numeroCompte) : "",
    iban: data.iban != null ? String(data.iban) : "",
    swift: data.swift != null ? String(data.swift) : "",
    deviseCompte: data.deviseCompte != null ? String(data.deviseCompte) : "",
    mobileMoneyEntreprise: data.mobileMoneyEntreprise != null ? String(data.mobileMoneyEntreprise) : "",
    mentionTva: data.mentionTva != null ? String(data.mentionTva) : "",
    exonereTva: nb(data.exonereTva, false),
    dateTauxChange: dateToInputVal(data.dateTauxChange),
    delaiPaiementJours: nn(data.delaiPaiementJours, null),
    penaliteRetard: data.penaliteRetard != null ? String(data.penaliteRetard) : "",
    validiteProformaJours: nn(data.validiteProformaJours, null),
    clauseContestation: data.clauseContestation != null ? String(data.clauseContestation) : "",
    piedPageFacture: data.piedPageFacture != null ? String(data.piedPageFacture) : "",
    directorGeneralName: data.directorGeneralName != null ? String(data.directorGeneralName) : "",
    directorGeneralTitle: data.directorGeneralTitle != null ? String(data.directorGeneralTitle) : "",
    nomSignataire: data.nomSignataire != null ? String(data.nomSignataire) : "",
    fonctionSignataire: data.fonctionSignataire != null ? String(data.fonctionSignataire) : "",
    signatureCachetUrl: data.signatureCachetUrl != null ? String(data.signatureCachetUrl) : "",
  };
}
